import { Box, Paper, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { Canceler } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { CollectionList } from '../../../components/CollectionList';
import { FixedTab, RouteTabs, VariableTab } from '../../../components/RouteTabs';
import SearchTextField, { ISearchTextFieldImperativeHandle } from '../../../components/SearchTextField';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { deleteCollection, duplicateCollection, getCollections, searchCollectionByTitle } from '../../../services/api/Collection';
import { ICollection } from '../../../services/types';
import { replace } from '../../../services/utils';
import { RouteNames } from '../../config';
import CollectionView from './CollectionView';
import useStyles from './styles';

interface IProps {}

interface IParamProps {
  trackingId?: string;
}

const Collection = (props: IProps & RouteComponentProps): JSX.Element => {
  const classes = useStyles();

  const path = RouteNames.Collection;
  const match = useRouteMatch<IParamProps>(`${path}/v/:trackingId`);

  const history = useHistory();
  const location = useLocation();
  const trackingId = match?.params?.trackingId;

  const initialTrackingIds = trackingId ? [trackingId] : [];
  const [editingTrackingIds, setEditingTrackingIds] = useState<string[]>(initialTrackingIds);

  const [collections, setCollections] = useState<ICollection[]>();
  const [collectionsTotalCount, setCollectionsTotalCount] = useState<number | undefined>(0);

  const [searchResultCollections, setSearchResultCollections] = useState<ICollection[] | undefined>();
  const [isOnboarding, setIsOnboarding] = useState<string>('');

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const canceler = useRef<Canceler>(() => undefined);
  const searchCanceler = useRef<Canceler>(() => undefined);
  const searchTextFieldRef = useRef<ISearchTextFieldImperativeHandle>();
  const initialRendered = useRef<boolean>(false);

  const fetchCollections = async (page = 0, rowsPerPage = 20) => {
    canceler.current();
    try {
      const [fetchPromise, cancel] = getCollections(page + 1, rowsPerPage, isOnboarding === '' ? undefined : Boolean(Number(isOnboarding)));
      canceler.current = cancel;

      const result = await fetchPromise;

      if (result.data?.collections) {
        setSearchResultCollections(undefined);
        setCollections(result.data.collections);
        setCollectionsTotalCount(result.data.totalCount);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (location.pathname === RouteNames.Collection) {
        !collections && fetchCollections();
      }
    };

    fetchData();

    return () => {
      canceler.current();
      searchCanceler.current();
    };
  }, [location.pathname, collections]);

  const searchCollection = async (keyword: string) => {
    searchCanceler.current();

    if (keyword === '') {
      fetchCollections();
      return;
    }

    try {
      const [fetchPromise, cancel] = searchCollectionByTitle(keyword, isOnboarding === '' ? undefined : Boolean(Number(isOnboarding)));
      searchCanceler.current = cancel;

      const result = await fetchPromise;

      if (result && result.status === 200 && result.data) {
        setSearchResultCollections(result.data.items.map(item => item.collection));
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  const clearSearchKeywordAndResult = () => {
    searchTextFieldRef.current?.clear();
    setSearchResultCollections(undefined);
  };

  const handleKeyPress = useCallback(
    (value: string) => {
      searchCollection(value);
    },
    [isOnboarding],
  );

  const handleSearchChange = useCallback((value: string) => {
    // keyword를 다 지운 순간에 어떻게 해야 할지 모르겠음.....
  }, []);

  const handleSearchCancel = useCallback(() => {
    fetchCollections();
  }, []);

  const handleIsOnboardingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsOnboarding(e.target.value);
  }, []);

  const handleRequestFetch = useCallback((page, rowsPerPage) => fetchCollections(page, rowsPerPage), [isOnboarding]);

  const handleDuplicationClick = useCallback(
    (col: ICollection) => {
      const onProceed = async () => {
        if (!collections || !col.trackingId) {
          return;
        }

        try {
          backdrop.show();
          const result = await duplicateCollection(col.trackingId);

          if (result.status === 201 && result.data) {
            const copiedCollection = result.data as ICollection;
            copiedCollection.notify = true;
            const cloned = [result.data, ...collections];

            setCollections(cloned);
            clearSearchKeywordAndResult();

            snackbar.success('Duplicated successfully.');
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: `Duplicate ${col.internalTitle || col.title}`,
        text: 'Are you sure you want to make a copy of this collection?',
        onProceed,
      });
    },
    [backdrop, snackbar, confirm, collections],
  );

  const handleRowClick = useCallback(
    (col: ICollection) => {
      if (!col || !col.trackingId) return;

      const { trackingId } = col;

      if (!editingTrackingIds.find(id => id === trackingId)) {
        setEditingTrackingIds([...editingTrackingIds, trackingId]);
      }
    },
    [editingTrackingIds],
  );

  const handleDelete = useCallback(
    (col: ICollection) => {
      const onProceed = async () => {
        if (!collections || !col.trackingId) {
          return;
        }

        const idx = collections.findIndex(c => c.trackingId === col.trackingId);

        try {
          backdrop.show();
          const result = await deleteCollection(col.trackingId);

          if (result.status === 200) {
            const cloned = [...collections];
            cloned.splice(idx, 1);

            setCollections(cloned);

            snackbar.success('Deleted successfully.');
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          snackbar.error(e);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: 'Delete',
        text: 'Are you sure you want to delete this collection?',
        onProceed,
      });
    },
    [backdrop, snackbar, confirm, collections],
  );

  const handleCreate = useCallback(
    data => {
      if (collections) {
        data.notify = true;
        setCollections([data, ...collections]);
      } else {
        fetchCollections();
      }

      clearSearchKeywordAndResult();
      history.push(RouteNames.Collection);

      snackbar.success('Saved successfully.');
    },
    [collections, history, snackbar],
  );

  const handleError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.message}`);
    },
    [snackbar],
  );

  const handleTabClose = useCallback(
    trackingId => {
      setEditingTrackingIds(editingTrackingIds.filter(id => id !== trackingId));
    },
    [editingTrackingIds],
  );

  const getTabLabel = useCallback(
    trackingId => {
      const c = collections?.find(c => c.trackingId === trackingId);
      const label = c ? `${c.internalTitle ? `${c.internalTitle} : ` : ''}${c.title}` : `VIEW ${trackingId}`;

      return label;
    },
    [collections],
  );

  const handleEdit = useCallback(
    data => {
      if (collections) {
        data.notify = true;
        const replaced = replace(collections, c => c.trackingId === data.trackingId, data);
        setCollections(replaced);
      }

      snackbar.success('Saved changes successfully.');
    },
    [collections, snackbar],
  );

  const getLinkUri = useCallback((trackingId?: string) => (trackingId ? `${RouteNames.Collection}/v/${trackingId}` : ''), []);

  const getTabUri = useCallback(trackingId => `v/${trackingId}`, []);

  const keyExtractor = useCallback(trackingId => `tab-floating-${trackingId}`, []);

  const variableTabRenderer = useCallback(trackingId => <CollectionView trackingId={trackingId} onCreated={handleEdit} onError={handleError} />, [
    handleEdit,
    handleError,
  ]);

  useEffect(() => {
    if (initialRendered.current) {
      if (!searchResultCollections) {
        fetchCollections();
      }
    } else {
      initialRendered.current = true;
    }
  }, [isOnboarding]);

  return (
    <div>
      <RouteTabs path={path}>
        <FixedTab label="All Collections">
          <Box className={classes.listTop}>
            <Box className={classes.optionsContainer}>
              <RadioGroup name="isOnboarding" value={isOnboarding} onChange={handleIsOnboardingChange} className={classes.optionsRadioButtonGroups}>
                <FormControlLabel value={''} control={<Radio color="primary" />} label="All Collections" />
                <FormControlLabel value={'0'} control={<Radio color="primary" />} label="Exclude Onboarding Collections" />
                <FormControlLabel value={'1'} control={<Radio color="primary" />} label="Onboarding Collections Only" />
              </RadioGroup>
            </Box>
            <Box className={classes.searchWrap}>
              <SearchTextField
                className={classes.searchTextField}
                placeholder="Type Collection Title / Tracking ID"
                onValueChange={handleSearchChange}
                onValueSubmit={handleKeyPress}
                onCancel={handleSearchCancel}
                ref={searchTextFieldRef}
                autoFocus
              />
            </Box>
          </Box>
          <Paper>
            <CollectionList
              collections={searchResultCollections || collections || []}
              totalCount={searchResultCollections && searchResultCollections.length > 0 ? undefined : collectionsTotalCount}
              onRequestFetch={handleRequestFetch}
              onDuplicateCollection={handleDuplicationClick}
              onSelectCollection={handleRowClick}
              onClickDeleteButton={handleDelete}
              getLinkUri={getLinkUri}
            />
          </Paper>
        </FixedTab>
        <FixedTab label="Create a Manual Collection" uri="create">
          <CollectionView onCreated={handleCreate} onError={handleError} />
        </FixedTab>
        <VariableTab<string>
          data={editingTrackingIds}
          label={getTabLabel}
          uri={getTabUri}
          onClose={handleTabClose}
          keyExtractor={keyExtractor}
          render={variableTabRenderer}
        />
      </RouteTabs>
    </div>
  );
};

export default Collection;
